/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

//LDS Order History refers to UNIT order history

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IGroup, IGroupDelivery, IGroups } from '@msdyn365-commerce-modules/order-management';
import { IOrderInformation } from '@msdyn365-commerce-modules/order-management';
import { ISalesLine } from '@msdyn365-commerce-modules/order-management';
import { ISalesOrder } from './components/get-sales-order';
import { IHeader, IList, IOrderHistoryViewProps } from './lds-order-history';
//@ts-ignore
import { Primary } from '@churchofjesuschrist/eden-buttons';
import styled from 'styled-components';

interface GroupDeliveryProps extends IGroupDelivery {
    salesLines?: any;
    resources?: any;
}
interface ListProps extends IList {
    resources?: any;
}
interface SalesProps extends ISalesOrder {
    resources?: any;
}
interface GroupProps extends IGroup {
    resources?: any;
}
interface GroupsProps extends IGroups {
    resources?: any;
}

export const OrderHistoryOrderInfomation: React.FC<IOrderInformation> = ({
    orderInformationProps,
    salesId,
    receiptId,
    channelName,
    createdDate,
    count,
    amount,
    channelReferenceId
}) => (
    <Node {...orderInformationProps}>
        {channelName}
        {salesId}
        {receiptId}
        {createdDate}
        {count}
        {amount}
        {channelReferenceId}
    </Node>
);

export const OrderHistoryGroupDelivery: React.FC<GroupDeliveryProps> = ({
    deliveryProps,
    heading,
    count,
    processing,
    address,
    trackingInfo,
    salesLines,
    resources
}) => {
    //@ts-ignore
    if (address && address.props) {
        if (salesLines.length === 0) {
            //@ts-ignore
            address.props.resource.shipToLabel = resources.delivereySalesLines;
        }
    }
    return (
        <Node {...deliveryProps}>
            {salesLines && salesLines.length !== 0 && heading}
            {/* {salesLines && salesLines.length === 0 && <div className='ms-order-history__sales-status'>{resources.deliveredSalesLines}</div>} */}
            {salesLines && salesLines.length === 0 && (
                <div>
                    <div className='ms-order-history__sales-status'>
                        {resources.deliveredSalesLines} {count}
                    </div>
                </div>
            )}
            {salesLines && salesLines.length !== 0 && count}
            {processing}
            {address}
            {trackingInfo}
        </Node>
    );
};

export const OrderHistoryGroup: React.FC<GroupProps> = ({ groupProps, delivery, salesLinesProps, salesLines, resources }) => {
    let deliveryLines;
    if (salesLines && salesLines[0]) {
        deliveryLines = salesLines.filter(saleLine => {
            if (saleLine.data.product) {
                //@ts-ignore
                return saleLine.data.product.ExtensionProperties[0].Value?.BooleanValue === false;
            } else {
                return;
            }
        });
    }
    return (
        <Node {...groupProps}>
            {delivery && <OrderHistoryGroupDelivery {...delivery} salesLines={deliveryLines} resources={resources} />}
            {salesLines && (
                <Node {...salesLinesProps}>
                    {salesLines.map((salesLine: ISalesLine) => (
                        <React.Fragment key={salesLine.data.salesLine.LineId}>{salesLine.salesLine}</React.Fragment>
                    ))}
                </Node>
            )}
        </Node>
    );
};

export const OrderHistoryGroups: React.FC<GroupsProps> = ({ groupsProps, groups, resources }) => (
    <Node {...groupsProps}>
        {groups.map((group: IGroup, index: number) => (
            <OrderHistoryGroup key={index} {...group} resources={resources} />
        ))}
    </Node>
);

export const OrderHistoryHeader: React.FC<IHeader> = ({ headerProps, heading, orderCountLabel, extraActions }) => (
    <Node {...headerProps}>
        {heading}
        {orderCountLabel}
        {extraActions}
    </Node>
);

export const OrderHistorySalesOrder: React.FC<SalesProps> = ({
    salesOrderProps,
    orderInfomation,
    groups,
    orderDetailsLink,
    expandProductsButton,
    resources
}) => (
    <Node {...salesOrderProps}>
        {orderInfomation && <OrderHistoryOrderInfomation {...orderInfomation} />}
        {groups && <OrderHistoryGroups {...groups} resources={resources} />}
        {expandProductsButton}
        {orderDetailsLink}
    </Node>
);

export const OrderHistoryList: React.FC<ListProps> = ({ listProps, salesOrders, resources }) => {
    return (
        <Node {...listProps}>
            {salesOrders &&
                salesOrders.map((salesOrder: ISalesOrder, index: number) => (
                    <OrderHistorySalesOrder key={index} {...salesOrder} resources={resources} />
                ))}
        </Node>
    );
};

const OrderHistoryView: React.FC<IOrderHistoryViewProps> = props => {
    const [skip, setSkip] = React.useState(5);
    const [account, setAccount] = React.useState<string>();
    const [loading, setLoading] = React.useState(false);
    const [newAccount, setNewAccount] = React.useState(false);
    const [shouldShowMore, setShouldShowMore] = React.useState(true);
    const [unitList, setUnitList] = React.useState<any>();
    const selectList = props.data.paymentTypes.result?.filter(result => {
        return result.ACCOUNTNUM !== '';
    });
    // selectList?.push({ ACCOUNTNUM: '01486559', PAYMENTMETHOD: 'test account', OPERATION: 0, CHANNEL: 0, Id: 1 });

    React.useEffect(() => {
        if (selectList && selectList.length > 0 && !account && unitList !== selectList) {
            setUnitList(selectList);
            setAccount(selectList[0].ACCOUNTNUM);
        }
        if (selectList && selectList.length > 0 && account) {
            props.getUnitOrderHistory(account, 0, undefined, unitList);
            setNewAccount(false);
        }
        sessionStorage.setItem('orderHistory', 'true');
    }, [account]);

    const handleSelect = (num: any) => {
        setNewAccount(true);
        if (account !== num) {
            setAccount(num);
            setSkip(5);
        }
    };

    const handleClick = async () => {
        setLoading(true);
        const res = await props.getUnitOrderHistory(account, skip, setShouldShowMore, unitList);
        // if (!props.viewState.connectionFailed) {
        setSkip(prevState => (prevState += 5));
        // }

        setLoading(false);

        return res;
    };

    const LoadingSpinner = styled.div`
        border: 6px solid #f3f3f3;
        border-radius: 50%;
        border-top: 5px solid rgb(83, 87, 91);
        width: 40px;
        height: 40px;
        -webkit-animation: spin 2s linear infinite;
        -webkit-animation: spin 2s linear infinite;
        animation: spin 2s linear infinite;

        @-webkit-keyframes spin {
            0% {
                -webkit-transform: rotate(0deg);
            }
            100% {
                -webkit-transform: rotate(360deg);
            }
        }
        @keyframes spin {
            0% {
                transform: rotate(0deg);
            }
            100% {
                transform: rotate(360deg);
            }
        }
    `;

    return (
        <Module {...props.orderHistoryProps}>
            {props.header && <OrderHistoryHeader {...props.header} />}
            <select name='' id='' onChange={e => handleSelect(e.target.value)}>
                <option value='' disabled selected></option>
                {selectList!.map((result: any, index: any) => {
                    if (selectList && selectList.length > 1 && index === 0) {
                        return (
                            <option value={result.ACCOUNTNUM} key={result.Id} selected>
                                {result.PAYMENTMETHOD}
                            </option>
                        );
                    } else {
                        return (
                            <option value={result.ACCOUNTNUM} key={result.Id} selected={account === result.ACCOUNTNUM ? true : false}>
                                {result.PAYMENTMETHOD}
                            </option>
                        );
                    }
                })}
            </select>
            {props.emptyMessage || newAccount ? (
                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                    <LoadingSpinner />
                </div>
            ) : null}
            {/* {alert && (
                <>
                    {alert}
                    {props.backToShoppingLink}
                </>
            )} */}
            {props.emptyMessage && selectList === undefined ? (
                <>
                    {props.emptyMessage}
                    {props.backToShoppingLink}
                </>
            ) : null}
            {props.list && <OrderHistoryList {...props.list} resources={props.resources} />}
            {props.table}
            {loading && (
                <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '10px' }}>
                    <LoadingSpinner />
                </div>
            )}
            {props.viewState.connectionFailed && (
                <h3 style={{ display: 'flex', justifyContent: 'space-around', marginTop: '10px' }}>{props.resources.connectionError}</h3>
            )}
            {shouldShowMore && !props.emptyMessage && (
                <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '10px' }}>
                    <Primary style={{ width: '100%' }} onClick={() => handleClick()}>
                        {props.resources.moreButtonText}
                    </Primary>
                </div>
            )}
        </Module>
    );
};

export default OrderHistoryView;
